<template>
  <el-date-picker
    v-model="result"
    :type="type"
    :align="align"
    start-placeholder="开始日期"
    end-placeholder="结束日期"
    :format="type === 'datetimerange'? 'yyyy-MM-dd HH:mm:ss': type === 'month' ? 'yyyy-MM' : 'yyyy-MM-dd'"
    :default-time="['00:00:00', '23:59:59']"
    @change="dateChange"
    :disabled="disabled"
    :picker-options="pickerOptions">
  </el-date-picker>
</template>

<script>
export default {
  name: 'dateRangePicker',
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    // 可选日期最小值与当前日期差值，-1表示不限制最小值，时间戳
    disabledBefore: {
      type: Number,
      default: -1
    },
    // 可选日期最大值与当前日期差值，-1表示不限制最大值，时间戳
    disabledAfter: {
      type: Number,
      default: -1
    },
    // 是否显示快捷选项
    showShortcuts: {
      type: Boolean,
      default: false
    },
    // 快捷按钮数组
    options: {
      type: Array,
      default () {
        return ['today', 'yesterday', '7days', '30days', 'this month', 'last month']
      }
    },
    align: {
      type: String,
      default: 'left'
    },
    value: {
      type: Array,
      default () {
        return []
      }
    },
    // 日期选择器类型，可选datetimerange及daterange
    type: {
      type: String,
      default: 'datetimerange'
    },
    maxRange: {
      type: Number,
      default: 0
    },
    afterLimit: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      result: [],
      pickerMinDate: ''
    }
  },
  watch: {
    value (val, oldVal) {
      this.pickerMinDate = ''
      if (JSON.stringify(val) !== '[]' || JSON.stringify(val) !== JSON.stringify(oldVal)) {
        this.result = val
      }
    }
  },
  computed: {
    pickerOptions () {
      const that = this
      const result = {}
      result.onPick = (obj) => {
        this.pickerMinDate = new Date(obj.minDate).getTime()
      }
      if (!this.disabledAfter && !this.disabledBefore && this.maxRange <= 0) {
        return result
      }
      result.disabledDate = (time) => {
        const before = time.getTime() < that.disabledBefore - 8.64e6
        const now = new Date()
        const after = that.afterLimit
          ? that.disabledAfter > 0 ? time.getTime() > that.disabledAfter : time.getTime() > new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
          : false
        if (this.maxRange > 0 && this.pickerMinDate) {
          const maxTime = this.pickerMinDate + this.maxRange * 24 * 60 * 60 * 1000
          const minTime = this.pickerMinDate - this.maxRange * 24 * 60 * 60 * 1000
          const outRange = time.getTime() >= maxTime || time.getTime() <= minTime
          return (before && after) || before || after || outRange
        } else {
          return (before && after) || before || after
        }
      }
      // 不显示快捷键直接返回选项参数
      if (!this.showShortcuts) {
        return result
      } else {
        result.shortcuts = []
      }
      if (this.options.includes('today')) {
        result.shortcuts.push({
          text: '今天',
          onClick (picker) {
            let end = new Date()
            let start = new Date()
            start = that.formatStart(start)
            end = that.formatEnd(end)
            picker.$emit('pick', [start, end])
          }
        })
      }
      if (this.options.includes('yesterday')) {
        result.shortcuts.push({
          text: '昨天',
          onClick (picker) {
            let end = new Date()
            let start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24)
            end.setTime(end.getTime() - 3600 * 1000 * 24)
            start = that.formatStart(start)
            end = that.formatEnd(end)
            picker.$emit('pick', [start, end])
          }
        })
      }
      if (this.options.includes('7days')) {
        result.shortcuts.push({
          text: '最近7天',
          onClick (picker) {
            let end = new Date()
            let start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 7)
            start = that.formatStart(start)
            end = that.formatEnd(end)
            picker.$emit('pick', [start, end])
          }
        })
      }
      if (this.options.includes('30days')) {
        result.shortcuts.push({
          text: '最近30天',
          onClick (picker) {
            let end = new Date()
            let start = new Date()
            start.setTime(start.getTime() - 3600 * 1000 * 24 * 30)
            start = that.formatStart(start)
            end = that.formatEnd(end)
            picker.$emit('pick', [start, end])
          }
        })
      }
      if (this.options.includes('this month')) {
        result.shortcuts.push({
          text: '这个月',
          onClick (picker) {
            let end = new Date()
            let start = new Date()
            const year = start.getFullYear()
            const month = start.getMonth()
            start = new Date(year, month)
            start = that.formatStart(start)
            end = that.formatEnd(end)
            picker.$emit('pick', [start, end])
          }
        })
      }
      if (this.options.includes('last month')) {
        result.shortcuts.push({
          text: '上个月',
          onClick (picker) {
            const now = new Date()
            const year = now.getFullYear()
            const month = now.getMonth()
            let start = new Date(year, month - 1)
            let end = new Date(now.setDate(0)) // 传0是上一个月的最后一天
            start = that.formatStart(start)
            end = that.formatEnd(end)
            picker.$emit('pick', [start, end])
          }
        })
      }
      return result
    }
  },
  mounted () {
    if (JSON.stringify(this.value) !== '[]') {
      this.result = this.value
    }
  },
  methods: {
    dateChange (value) {
      if (value !== null) {
        // value[0] = value[0].getFullYear() + '-' + (((value[0].getMonth() + 1) < 10)  ? ('0'+ (value[0].getMonth() + 1)) : (value[0].getMonth() + 1))+ '-' + ((value[0].getDate() < 10) ? ('0' + value[0].getDate()) : (value[0].getDate())) + ' 00:00:00';
        // value[1] = value[1].getFullYear() + '-' + (((value[1].getMonth() + 1) < 10)  ? ('0'+ (value[1].getMonth() + 1)) : (value[1].getMonth() + 1)) + '-' + ((value[1].getDate() < 10) ? ('0' + value[1].getDate()) : (value[1].getDate())) + ' 23:59:59';
        if (this.type === 'datetimerange') {
          value[0] = this.parseTime(value[0], '{y}-{m}-{d} {h}:{i}:{s}')
          value[1] = this.parseTime(value[1], '{y}-{m}-{d} {h}:{i}:{s}')
        } else if (this.type === 'month') {
          value = this.parseTime(value, '{y}-{m}-{d}')
        } else {
          value[0] = this.parseTime(value[0], '{y}-{m}-{d}')
          value[1] = this.parseTime(value[1], '{y}-{m}-{d}')
        }
        this.result = value
      } else {
        this.result = []
      }
      this.pickerMinDate = ''
      this.$emit('getChange', this.result)
    },
    formatStart (start) {
      start.setHours(0)
      start.setMinutes(0)
      start.setSeconds(0)
      return start
    },
    formatEnd (end) {
      end.setHours(23)
      end.setMinutes(59)
      end.setSeconds(59)
      return end
    },
    parseTime (time, cFormat) {
      const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}'
      let date
      if (typeof time === 'object') {
        date = time
      } else {
        if (('' + time).length === 10) time = parseInt(time) * 1000
        date = new Date(time)
      }
      const formatObj = {
        y: date.getFullYear(),
        m: date.getMonth() + 1,
        d: date.getDate(),
        h: date.getHours(),
        i: date.getMinutes(),
        s: date.getSeconds(),
        a: date.getDay()
      }
      const timeStr = format.replace(/{(y|m|d|h|i|s|a)+}/g, (result, key) => {
        let value = formatObj[key]
        if (key === 'a') {
          return ['一', '二', '三', '四', '五', '六', '日'][value - 1]
        }
        if (result.length > 0 && value < 10) {
          value = '0' + value
        }
        return value || 0
      })
      return timeStr
    }
  }
}
</script>

<style scoped>

</style>
